import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ProductsByEngineModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { BarSegment } from '../SegmentedBarChart'

import { useTranslation } from 'react-i18next'

import { useProductCountByEngineStatisticsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { PricingEngineType } from '@/shared/generated'
import { PALETTE } from '@/shared/lib/theme-palette'
import { cn } from '@/shared/lib/utils'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'
import { SegmentedBarChart } from '../SegmentedBarChart'

export default function PricingDistributionSegmentedWidget({
	commonFilters,
	className,
}: {
	commonFilters: FilterSchema[]
	className?: string
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const {
		data: segments,
		isLoading,
		isError,
	} = useProductCountByEngineStatisticsQuery(
		{
			filters: commonFilters,
			currency,
		},
		{
			select: (data) => toSegments(data, t),
		}
	)
	const hasData = !!segments?.length

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.pricing_distribution.title', {
					defaultValue: 'Pricing Distribution',
				})}
				tooltip={t('widget.pricing_distribution.tooltip', {
					defaultValue:
						'Count all SKU and POS combinations grouped by pricing approach and campaign type',
				})}
				subtitle={t('widget.pricing_distribution.description', {
					defaultValue: 'Price points by pricing campaign types (SKU * POS)',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				{isLoading && <Skeleton className='col-span-2 h-10 w-full' />}
				{hasData && !isLoading && (
					<SegmentedBarChart segments={segments} showLegend height={100} />
				)}
				{!hasData && !isLoading && <WidgetEmptyState size='small' />}
			</WidgetContent>
		</Widget>
	)
}
const getSerieColor = (engine: PricingEngineType | undefined) => {
	switch (engine) {
		case PricingEngineType.RB:
			return PALETTE.orange[75]
		case PricingEngineType.ML:
			return PALETTE.blue[75]
		case PricingEngineType.MD:
			return PALETTE.purple[75]
		case PricingEngineType.Promo:
			return PALETTE.green[75]
		default:
			return PALETTE.neutral[150]
	}
}

const toSegments = (
	data: ProductsByEngineModel[] | null,
	t: TFunction
): BarSegment[] =>
	(data ?? []).map((item) => ({
		key: item.pricing_campaign_engine ?? 'unassigned',
		label: t(item.pricing_campaign_engine ?? 'general_unassigned', {
			defaultValue: item.pricing_campaign_engine ?? t('general_unassigned'),
		}),
		value: item.price_points ?? 0,
		percent: item.price_points_percent || 0,
		color: getSerieColor(item.pricing_campaign_engine as PricingEngineType),
	}))
